import Phaser from 'phaser';
import { getTalent, TALENT_MAX, TalentTarget } from '../talents';
import { DEPTH, TINT } from '../ui/theme';

export default class Player extends Phaser.Physics.Arcade.Sprite implements TalentTarget {
  hp = 100;

  // ── 派生属性（全部由天赋重新计算，见 recomputeStats）─────────
  maxHp = 100;
  moveSpeed = 160;
  damageMul = 1;
  fireRate = 250;
  extraBullets = 0;
  pierce = 0;
  critChance = 0;
  critMul = 2;
  lifesteal = 0;
  healPerHit = 0;
  regenPerFloor = 0;
  thorns = 0;
  damageReduction = 0;
  dropMul = 1;
  scoreMul = 1;
  pickupRadius = 0;
  knockbackResist = 0;
  eliteDmgMul = 1;
  slowAmount = 0;
  bulletSpeed = 320;

  /** 已学习的天赋：id -> 叠加百分值(0..100)，槽数随层数解锁 */
  talents = new Map<string, number>();

  /** 当前朝向（弧度）。无敌人时按此方向射击，默认朝右。 */
  facingAngle = 0;
  /** 击退冷却（避免被连续击退钉在原地） */
  knockbackUntil = 0;
  /** 受伤特效剩余时间（ms，delta 驱动，不依赖 Clock） */
  private hurtTimer = 0;
  private static readonly HURT_FLASH_MS = 150;

  // ── 无敌护盾（进出门时获得，护盾直径 = 角色 3 倍）──────────
  /** 无敌剩余时间（ms）；>0 时免疫一切伤害 */
  private invulnTimer = 0;
  private shieldElapsed = 0;
  private shield?: Phaser.GameObjects.Image;
  /** 护盾相对角色的倍数（用户要求 3 倍） */
  static readonly SHIELD_MUL = 3;
  /** 默认无敌时长（ms） */
  static readonly INVULN_MS = 3000;

  /** 属性的基准值（无任何天赋时的起点） */
  private static readonly BASE = {
    maxHp: 100,
    moveSpeed: 160,
    damageMul: 1,
    fireRate: 250,
    extraBullets: 0,
    pierce: 0,
    critChance: 0,
    critMul: 2,
    lifesteal: 0,
    healPerHit: 0,
    regenPerFloor: 0,
    thorns: 0,
    damageReduction: 0,
    dropMul: 1,
    scoreMul: 1,
    pickupRadius: 0,
    knockbackResist: 0,
    eliteDmgMul: 1,
    slowAmount: 0,
    bulletSpeed: 320,
  };

  constructor(scene: Phaser.Scene, x: number, y: number, key: string) {
    super(scene, x, y, key);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setCollideWorldBounds(true);
    // 素材为 32×32，按一格的 28px 显示更贴合墙体/怪物比例
    this.setDisplaySize(28, 28);
    // 碰撞体：世界像素 16×16，并与贴图中心对齐。
    // 源尺寸需除以缩放（否则 updateFromGameObject 后会被再次乘缩放）。
    // 若只 setSize 而不同步，机体会偏离贴图中心约半格，
    // 让玩家在一侧能贴进墙里（“穿墙”感）。
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(16 / this.scaleX, 16 / this.scaleY);
    body.updateFromGameObject();

    // 无敌护盾实体（默认隐藏，进出门时显示）
    if (scene.textures.exists('fx-shield')) {
      this.shield = scene.add.image(x, y, 'fx-shield')
        .setTint(TINT.shield)
        .setDepth(DEPTH.worldLabel)
        .setVisible(false);
    }

    this.recomputeStats();

    // 初始播放待机动画（动画已在 Preload 中创建）
    if (scene.anims.exists('player-idle')) this.anims.play('player-idle', true);
  }

  /** 获得无敌护盾（取较大值，重复触发会刷新时长） */
  grantInvincibility(ms = Player.INVULN_MS) {
    this.invulnTimer = Math.max(this.invulnTimer, ms);
    this.shieldElapsed = 0;
    if (this.shield) {
      this.shield.setVisible(true).setPosition(this.x, this.y);
      this.resizeShield(1);
    }
  }

  isInvincible(): boolean {
    return this.invulnTimer > 0;
  }

  /** 护盾直径 = 角色宽 × SHIELD_MUL */
  private resizeShield(pulse: number) {
    if (!this.shield) return;
    const size = (this.displayWidth || 16) * Player.SHIELD_MUL * pulse;
    this.shield.setDisplaySize(size, size);
  }

  /** 护盾跟随 + 倒计时（delta 驱动，任何环境都会到期消失） */
  updateShield(delta: number) {
    if (this.invulnTimer <= 0) return;
    this.invulnTimer -= delta;
    this.shieldElapsed += delta;

    if (this.shield) {
      this.shield.setPosition(this.x, this.y);
      const pulse = 1 + Math.sin(this.shieldElapsed / 120) * 0.05;
      this.resizeShield(pulse);
      // 剩余不足 700ms 时快速闪烁，提示即将结束
      const a = this.invulnTimer < 700
        ? 0.3 + 0.5 * Math.abs(Math.sin(this.shieldElapsed / 70))
        : 0.75;
      this.shield.setAlpha(a);
    }

    if (this.invulnTimer <= 0) {
      this.invulnTimer = 0;
      this.shield?.setVisible(false);
    }
  }

  /** 依据当前天赋重算全部属性（先回到基准值，再逐个天赋叠加） */
  recomputeStats() {
    const b = Player.BASE;
    this.maxHp = b.maxHp;
    this.moveSpeed = b.moveSpeed;
    this.damageMul = b.damageMul;
    this.fireRate = b.fireRate;
    this.extraBullets = b.extraBullets;
    this.pierce = b.pierce;
    this.critChance = b.critChance;
    this.critMul = b.critMul;
    this.lifesteal = b.lifesteal;
    this.healPerHit = b.healPerHit;
    this.regenPerFloor = b.regenPerFloor;
    this.thorns = b.thorns;
    this.damageReduction = b.damageReduction;
    this.dropMul = b.dropMul;
    this.scoreMul = b.scoreMul;
    this.pickupRadius = b.pickupRadius;
    this.knockbackResist = b.knockbackResist;
    this.eliteDmgMul = b.eliteDmgMul;
    this.slowAmount = b.slowAmount;
    this.bulletSpeed = b.bulletSpeed;

    this.talents.forEach((v, id) => {
      getTalent(id)?.apply(this, v);
    });

    // 属性下降（放弃天赋）后生命不得超过上限
    this.hp = Math.min(this.hp, this.maxHp);
  }

  /** 学习 / 叠加一个天赋（值上限 100%） */
  addTalent(id: string, value: number) {
    this.talents.set(id, Math.min(TALENT_MAX, value));
    this.recomputeStats();
  }

  /** 放弃一个天赋 */
  dropTalent(id: string) {
    this.talents.delete(id);
    this.recomputeStats();
  }

  move(
    cursors: Phaser.Types.Input.Keyboard.CursorKeys,
    wasd: any,
    delta: number
  ) {
    const speed = this.moveSpeed * (delta / 16);
    let vx = 0;
    let vy = 0;

    if (cursors.left.isDown || (wasd as any).left.isDown) vx -= speed;
    if (cursors.right.isDown || (wasd as any).right.isDown) vx += speed;
    if (cursors.up.isDown || (wasd as any).up.isDown) vy -= speed;
    if (cursors.down.isDown || (wasd as any).down.isDown) vy += speed;

    if (vx !== 0 && vy !== 0) {
      vx *= 0.707;
      vy *= 0.707;
    }

    (this.body as Phaser.Physics.Arcade.Body).setVelocity(vx, vy);

    // 记录朝向：无敌人时朝这个方向射击
    if (vx !== 0 || vy !== 0) this.facingAngle = Math.atan2(vy, vx);

    // 动画：移动时走路，静止时待机
    this.playMoveAnim(vx !== 0 || vy !== 0);

    if (vx > 0) this.setFlipX(false);
    else if (vx < 0) this.setFlipX(true);

    // 受伤特效衰减（红闪 + 闪烁），用 delta 推进，不依赖 scene.time
    if (this.hurtTimer > 0) {
      this.hurtTimer -= delta;
      if (this.hurtTimer <= 0) {
        this.hurtTimer = 0;
        this.setAlpha(1);
        if (this.hp > 0) this.clearTint();
      } else {
        // 0.45 → 1 渐变回去
        this.setAlpha(0.45 + 0.55 * (1 - this.hurtTimer / Player.HURT_FLASH_MS));
      }
    }
  }

  /** 每发子弹的伤害（不含暴击） */
  get bulletDamage(): number {
    return 25 * this.damageMul;
  }

  /** 移动/待机/受伤动画切换（动画不存在时静默跳过） */
  private playMoveAnim(moving: boolean) {
    let key = moving ? 'player-run' : 'player-idle';
    if (this.hurtTimer > 0 && this.scene.anims.exists('player-hit')) key = 'player-hit';
    if (this.anims.currentAnim?.key === key) return;
    if (this.scene.anims.exists(key)) this.anims.play(key, true);
  }

  /**
   * 受伤。不再使用全局无敌帧 —— 每只敌人按自己的攻击间隔独立结算，
   * 因此「被几只怪同时贴脸」= 真的痛好几倍。铁甲天赋提供减伤。
   */
  takeDamage(amount: number): boolean {
    if (amount <= 0) return false;
    // 无敌护盾期间免疫一切伤害（进出门保护）
    if (this.invulnTimer > 0) return false;
    const reduced = amount * (1 - Math.min(0.9, this.damageReduction));
    this.hp = Math.max(0, this.hp - reduced);
    this.hurtFx();
    return true;
  }

  /** 受伤动态特效：红闪 + 火花 + 闪烁 + 轻微震屏（衰减在 move() 里用 delta 推进） */
  private hurtFx() {
    // 命中火花
    if (this.scene.textures.exists('fx-spark')) {
      const spark = this.scene.add.image(this.x, this.y, 'fx-spark')
        .setTint(0xff5555).setDepth(75).setScale(0.9);
      this.scene.tweens.add({
        targets: spark, alpha: 0, scale: 1.9, duration: 220,
        onComplete: () => spark.destroy(),
      });
    }

    // 红闪 + 闪烁（死亡时保留死亡红，不自动清除）
    this.setTint(0xff6666);
    this.setAlpha(0.45);
    this.hurtTimer = this.hp > 0 ? Player.HURT_FLASH_MS : 0;

    // 轻微震屏
    this.scene.cameras.main.shake(90, 0.006);
  }

  heal(amount: number) {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }
}
